/**
 * Document picker for the Submission Pack link cell (Task B4).
 *
 * Lists what the user could already attach: the bid's own documents first, then
 * the org library. Both come from the EXISTING list endpoints the Documents tab
 * and the Library page already call — the pack adds no document route of its
 * own.
 *
 * The two reads are deliberately local to this popover rather than routed
 * through `documents-store` / `library-store`: those stores own the state of
 * their own pages (selection, sort, paging, in-flight uploads), and a picker
 * that hijacked them would move the user's place on another tab.
 */

import { useEffect, useMemo, useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { api } from '@/lib/api'
import { formatFileSize, type Document } from '@/lib/schemas'

// ===================
// Types
// ===================

interface DocumentLinkPickerProps {
  bidId: string
  onSelect: (documentId: string) => void | Promise<void>
}

interface PickerDocument {
  id: string
  name: string
  size: number
}

interface DocumentGroup {
  label: string
  documents: PickerDocument[]
}

/** Matches the library handler's list shape. */
interface LibraryListResponse {
  documents: Document[]
}

/** Page size the library list already uses. */
const LIBRARY_PAGE_SIZE = 100

function toPickerDocuments(documents: Document[] | undefined): PickerDocument[] {
  return (documents ?? []).map((document) => ({
    id: document.id,
    name: document.name,
    size: document.size,
  }))
}

// ===================
// Component
// ===================

export function DocumentLinkPicker({ bidId, onSelect }: DocumentLinkPickerProps) {
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [bidDocuments, setBidDocuments] = useState<PickerDocument[]>([])
  const [libraryDocuments, setLibraryDocuments] = useState<PickerDocument[]>([])

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)

    void Promise.all([
      api
        .get<Document[]>(`/bids/${bidId}/documents`)
        .catch(() => [] as Document[]),
      api
        .get<LibraryListResponse>('/library/documents', {
          limit: String(LIBRARY_PAGE_SIZE),
          offset: '0',
        })
        .catch(() => ({ documents: [] as Document[] })),
    ]).then(([bidDocs, libraryResponse]) => {
      if (cancelled) return
      setBidDocuments(toPickerDocuments(bidDocs))
      setLibraryDocuments(toPickerDocuments(libraryResponse.documents))
      setIsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [bidId])

  const groups = useMemo<DocumentGroup[]>(() => {
    const query = search.trim().toLowerCase()
    const filter = (documents: PickerDocument[]) =>
      query
        ? documents.filter((document) => document.name.toLowerCase().includes(query))
        : documents

    // A library document may also be attached to the bid; the bid copy wins so
    // the same file never appears twice in one list.
    const bidIds = new Set(bidDocuments.map((document) => document.id))

    return [
      { label: 'Bid documents', documents: filter(bidDocuments) },
      {
        label: 'Library',
        documents: filter(libraryDocuments.filter((d) => !bidIds.has(d.id))),
      },
    ].filter((group) => group.documents.length > 0)
  }, [bidDocuments, libraryDocuments, search])

  return (
    <div className="w-80">
      <div className="border-b p-2">
        <Input
          placeholder="Search documents..."
          className="h-8 text-sm"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="max-h-64 overflow-y-auto py-1">
        {isLoading ? (
          <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading documents...
          </div>
        ) : groups.length === 0 ? (
          <div className="px-3 py-3 text-sm text-muted-foreground">
            No matching documents yet.
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.label} className="px-1">
              <p className="text-meta px-2 py-1">{group.label}</p>
              {group.documents.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => void onSelect(document.id)}
                >
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{document.name}</span>
                  <span className="text-meta shrink-0">
                    {formatFileSize(document.size)}
                  </span>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
